// Live classes (/class): page data, registration, calendar links.
// Server side: supabase/migrations/20261103_class_registrations.sql and
// functions/api/class/register.ts (sends the confirmation + host emails).
import { supabase } from '../config/supabase';

/** The class /class shows (and the banners link to). */
export const CURRENT_CLASS = {
  slug: 'build-with-ai',
  title: 'Build on GathrXR with ChatGPT or Claude',
  startsAt: '2026-10-30T23:00:00Z', // Fri Oct 30 2026, 7:00 PM Eastern
  minutes: 90,
};

/** True until the current class ends — banners hide themselves after that. */
export const classBannerLive = (now = Date.now()) =>
  now < new Date(CURRENT_CLASS.startsAt).getTime() + CURRENT_CLASS.minutes * 60000;

export type AiTool = 'chatgpt' | 'claude' | 'both' | 'unsure';
export type Experience = 'new' | 'some' | 'experienced';

export interface ClassRegistration {
  name: string | null; email: string; ai_tool: AiTool | null; experience: Experience | null;
  goals: string | null; created_at: string;
}
export interface ClassEvent {
  id: string; slug: string; title: string; description: string | null; host_name: string | null;
  starts_at: string; duration_minutes: number; is_open: boolean; ended: boolean;
  registration: ClassRegistration | null;
  zoom: { url: string | null; meeting_id: string | null; passcode: string | null } | null;
}

export const AI_TOOLS: { id: AiTool; label: string }[] = [
  { id: 'chatgpt', label: 'ChatGPT' }, { id: 'claude', label: 'Claude' }, { id: 'both', label: 'Both' }, { id: 'unsure', label: 'Not sure yet' },
];
export const EXPERIENCE: { id: Experience; label: string }[] = [
  { id: 'new', label: 'New to building' }, { id: 'some', label: 'Built a little' }, { id: 'experienced', label: 'Experienced builder' },
];

export async function getClassEvent(slug: string): Promise<ClassEvent | null> {
  const { data, error } = await supabase.rpc('get_class_event', { p_slug: slug });
  if (error) throw new Error('Couldn’t load the class. Check your connection and try again.');
  return (data as ClassEvent | null) ?? null;
}

export interface RegisterAnswers { name?: string; ai_tool?: AiTool | ''; experience?: Experience | ''; goals?: string }

export async function registerForClass(slug: string, a: RegisterAnswers): Promise<{ event: ClassEvent; is_new: boolean; emailed: boolean }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Sign in first.');
  let res: Response;
  try {
    res = await fetch('/api/class/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-gathrxr-token': session.access_token },
      body: JSON.stringify({ slug, ...a }),
    });
  } catch {
    throw new Error('Can’t reach GathrXR right now. Check your connection.');
  }
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.ok) throw new Error(body?.error || 'Couldn’t register right now. Please try again.');
  return { event: body.event as ClassEvent, is_new: !!body.is_new, emailed: !!body.emailed };
}

// ─── Answers typed before signing up (kept across the email-confirm trip) ───
const PENDING_KEY = 'gx-class-pending';
export function savePendingAnswers(slug: string, a: RegisterAnswers) {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify({ slug, a, at: Date.now() })); } catch { /* private mode */ }
}
export function readPendingAnswers(slug: string): RegisterAnswers | null {
  try {
    const v = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
    if (!v || v.slug !== slug || Date.now() - v.at > 14 * 86400000) return null;
    return v.a as RegisterAnswers;
  } catch { return null; }
}
export function clearPendingAnswers() { try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ } }

// ─── Calendar ───────────────────────────────────────────────────────────────
const stamp = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
const escText = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

function calDetails(ev: ClassEvent): { description: string; location: string } {
  const z = ev.zoom;
  const page = `${window.location.origin}/class`;
  const zoom = z?.url ? [`Join on Zoom: ${z.url}`, z.meeting_id && `Meeting ID: ${z.meeting_id}`, z.passcode && `Passcode: ${z.passcode}`].filter(Boolean) as string[] : [];
  return {
    description: [...zoom, '', `Class page: ${page}`, 'Bring: a ChatGPT or Claude account and your GathrXR account.'].join('\n'),
    location: z?.url || page,
  };
}

export function googleCalendarUrl(ev: ClassEvent): string {
  const start = new Date(ev.starts_at);
  const end = new Date(start.getTime() + ev.duration_minutes * 60000);
  const { description, location } = calDetails(ev);
  const p = new URLSearchParams({ action: 'TEMPLATE', text: ev.title, dates: `${stamp(start)}/${stamp(end)}`, details: description, location });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

export function downloadIcs(ev: ClassEvent) {
  const start = new Date(ev.starts_at);
  const end = new Date(start.getTime() + ev.duration_minutes * 60000);
  const { description, location } = calDetails(ev);
  const fold = (l: string) => { const o: string[] = []; let r = l; while (r.length > 74) { o.push(r.slice(0, 74)); r = ' ' + r.slice(74); } o.push(r); return o.join('\r\n'); };
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//GathrXR//Classes//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    'BEGIN:VEVENT', `UID:${ev.slug}-${ev.id}@gathrxr.com`, `DTSTAMP:${stamp(new Date())}`,
    `DTSTART:${stamp(start)}`, `DTEND:${stamp(end)}`, `SUMMARY:${escText(ev.title)}`,
    `DESCRIPTION:${escText(description)}`, `LOCATION:${escText(location)}`,
    'BEGIN:VALARM', 'TRIGGER:-PT30M', 'ACTION:DISPLAY', `DESCRIPTION:${escText(ev.title)}`, 'END:VALARM',
    'END:VEVENT', 'END:VCALENDAR',
  ].map(fold).join('\r\n') + '\r\n';
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = 'gathrxr-class.ics';
  document.body.appendChild(a); a.click(); a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** "Fri, Oct 30, 7:00 PM" in a zone (the viewer's by default). */
export function fmtClassTime(iso: string, tz?: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(tz ? { timeZone: tz } : {}) });
  } catch { return new Date(iso).toLocaleString(); }
}
